// Renderer selection and capabilities stay at the registry boundary.
import {
    type ApprovalEditor,
    type ApprovalPresentation,
} from '@apps/web/features/phoebe_chat_v3/registry/types';

export type ApprovalRenderer =
    | Readonly<{
          kind: 'shift_write';
          editor: Extract<ApprovalEditor, { kind: 'shift_write' }>;
      }>
    | Readonly<{
          kind: 'settings';
          editor: Extract<ApprovalEditor, { kind: 'settings' }>;
      }>
    | Readonly<{
          kind: 'ehr_person_note';
          editor: Extract<ApprovalEditor, { kind: 'ehr_person_note' }>;
      }>
    | Readonly<{ kind: 'legacy' }>
    | Readonly<{ kind: 'communication'; channel: 'sms' | 'voice' }>
    | Readonly<{
          kind: 'assignment';
          editor: Extract<ApprovalEditor, { kind: 'assign_fill' }>;
      }>
    | Readonly<{
          kind: 'legacy_assignment';
          caregiverName: string;
          shifts: ReadonlyArray<{ id: string; name: string; detail: string }>;
      }>
    | Readonly<{ kind: 'outreach' }>
    | Readonly<{ kind: 'save_note' }>
    | Readonly<{ kind: 'generic' }>;

/** Select once for pending, resolved and expired states. Legacy presentations
 * still identify their renderer by question text; keep that compatibility
 * parsing here so receipts cannot drift from the original card's surface. */
export function approvalRenderer(
    editor: ApprovalEditor | null,
    presentation: ApprovalPresentation | null,
    batch: boolean,
): ApprovalRenderer {
    if (!batch && editor?.kind === 'shift_write') {
        return { kind: 'shift_write', editor };
    }
    if (!batch && editor?.kind === 'settings') {
        return { kind: 'settings', editor };
    }
    if (!batch && editor?.kind === 'ehr_person_note') {
        return { kind: 'ehr_person_note', editor };
    }
    if (presentation === null) {
        return { kind: 'legacy' };
    }
    if (editor?.kind === 'save_note') {
        return { kind: 'save_note' };
    }
    const question = presentation.question;
    if (question === 'Can I send these texts?') {
        return { kind: 'communication', channel: 'sms' };
    }
    if (/^Can I call .+\?$/.test(question)) {
        return { kind: 'communication', channel: 'voice' };
    }
    if (!batch && editor?.kind === 'assign_fill') {
        return { kind: 'assignment', editor };
    }
    const assignMatch =
        /^can I assign (.+?) to (?:this shift|these shifts)\?$/.exec(
            question,
        ) ??
        /^can I assign (.+?) and fill (?:this shift|these shifts)\?$/.exec(
            question,
        );
    if (assignMatch !== null) {
        const caregiverName = assignMatch[1] ?? '';
        const shifts = presentation.groups.flatMap((group, groupIndex) => {
            if (!group.heading.startsWith('Client: ')) {
                return [];
            }
            const clientName = group.heading.slice('Client: '.length);
            return group.items.map((item, itemIndex) => ({
                id: `${groupIndex}:${itemIndex}`,
                name: clientName,
                detail: item,
            }));
        });
        if (caregiverName !== '' && shifts.length > 0) {
            return { kind: 'legacy_assignment', caregiverName, shifts };
        }
    }
    if (
        editor?.kind === 'outreach' ||
        /^can I start this outreach to \d+ caregivers?\?$/.test(question)
    ) {
        return { kind: 'outreach' };
    }
    if (
        /^(?:Can I|Save)(?: this)? (?:caregiver |client )?(?:note|information)\??$/i.test(
            question,
        )
    ) {
        return { kind: 'save_note' };
    }
    return { kind: 'generic' };
}

export function approvalStatusPlacement(
    editor: ApprovalEditor | null,
    presentation: ApprovalPresentation | null,
): 'inline' | 'shell' {
    const renderer = approvalRenderer(editor, presentation, false);
    return renderer.kind === 'assignment' ||
        renderer.kind === 'legacy_assignment'
        ? 'inline'
        : 'shell';
}
